import { Component, ErrorInfo, ReactNode } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";

interface Props {
    children: ReactNode;
    /** Extra context to attach to the crash log (e.g. form/session details). */
    context?: Record<string, unknown>;
}

interface State {
    hasError: boolean;
    details: Record<string, unknown> | null;
}

/**
 * Wraps the assessment view so a render/commit crash (e.g. the intermittent
 * `insertBefore: NotFoundError` reported by the Spanish client) is caught,
 * logged, AND shown on screen with the environment details needed to
 * root-cause it — so the client can simply screenshot the page and share it
 * instead of us needing devtools access.
 *
 * The captured signals distinguish the competing hypotheses:
 *  - `htmlClass`/`translate` -> is the browser translating the page (Google
 *    Translate mutates DOM text nodes and causes exactly this crash)?
 *  - `navLang`/`lang` -> the client's browser locale vs the page language.
 *  - `buildStamp` -> whether the client is on the current deploy or a stale
 *    service-worker cached bundle (explains "still crashing after the fix").
 *  - `componentStack` -> the real (un-minified in dev) component path.
 */
class AssessmentErrorBoundary extends Component<Props, State> {
    state: State = { hasError: false, details: null };

    static getDerivedStateFromError(): Partial<State> {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        const root = document.documentElement;
        const details: Record<string, unknown> = {
            message: error?.message,
            name: error?.name,
            // Positions here are resolvable via the prod source maps, unlike
            // React's `componentStack` (minified component names).
            stack: error?.stack,
            componentStack: info?.componentStack,
            // Google Translate adds a `translated-ltr`/`translated-rtl` class
            // and flips the `translate` attribute when it is rewriting the DOM.
            htmlClass: root.className,
            translate: root.getAttribute("translate"),
            lang: root.getAttribute("lang"),
            navLang: navigator.language,
            navLangs: navigator.languages,
            buildStamp:
                typeof __BUILD_STAMP__ !== "undefined"
                    ? __BUILD_STAMP__
                    : "unknown",
            time: new Date().toISOString(),
            url: window.location.href,
            userAgent: navigator.userAgent,
            ...this.props.context,
        };
        // eslint-disable-next-line no-console
        console.error("ASSESSMENT_CRASH", details);
        this.setState({ details });
    }

    handleCopy = (): void => {
        navigator.clipboard
            ?.writeText(JSON.stringify(this.state.details, null, 2))
            .catch(() => {});
    };

    render(): ReactNode {
        if (!this.state.hasError) {
            return this.props.children;
        }

        const { details } = this.state;

        return (
            <Box
                sx={{
                    height: "100%",
                    width: "100%",
                    overflow: "auto",
                    p: { xs: 2, md: 4 },
                }}
            >
                <Stack gap={2} sx={{ maxWidth: 900, mx: "auto" }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 600 }}>
                        Something went wrong while loading this assessment
                    </Typography>
                    <Typography color="textColors.666">
                        Please copy the details below and share it with the
                        Convin team.
                    </Typography>

                    <Box
                        component="pre"
                        sx={{
                            m: 0,
                            p: 2,
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: (theme) =>
                                theme.palette.mode === "dark"
                                    ? "grey.900"
                                    : "grey.100",
                            fontFamily: "monospace",
                            fontSize: 12,
                            lineHeight: 1.5,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            maxHeight: 480,
                            overflow: "auto",
                        }}
                    >
                        {JSON.stringify(details, null, 2)}
                    </Box>

                    <Stack direction="row" gap={1.5}>
                        <Button variant="outlined" onClick={this.handleCopy}>
                            Copy details
                        </Button>
                    </Stack>
                </Stack>
            </Box>
        );
    }
}

export default AssessmentErrorBoundary;
